import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { APPLICATION_LABELS, DOC_LABELS, formatSlotTime } from '../utils/serviceApplications';

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Notes are stored as "Label: value" lines; split them into form rows.
const parseNotes = (notes) => {
  const fields = [];
  const remarks = [];
  let documents = '';
  String(notes || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const idx = line.indexOf(':');
      if (idx === -1) {
        remarks.push(line);
        return;
      }
      const label = line.slice(0, idx).trim();
      const value = line.slice(idx + 1).trim();
      if (label.toLowerCase() === 'documents') documents = value;
      else fields.push([label, value]);
    });
  return { fields, remarks, documents };
};

const Row = ({ label, value }) => (
  <tr>
    <th>{label}</th>
    <td>{value || '—'}</td>
  </tr>
);

// Bank-style application form followed by every uploaded document on its own page.
// Rendered in a portal on <body>; print CSS hides #root and shows only this sheet.
const ApplicationPrint = ({ row, docPages, onDone }) => {
  useEffect(() => {
    document.body.classList.add('printing-application');
    const finish = () => onDone();
    window.addEventListener('afterprint', finish);
    // Let the images decode before the print dialog opens.
    const images = Array.from(document.querySelectorAll('.print-sheet img'));
    Promise.all(images.map((img) => (img.decode ? img.decode().catch(() => null) : null))).then(() => {
      window.print();
    });
    return () => {
      window.removeEventListener('afterprint', finish);
      document.body.classList.remove('printing-application');
    };
  }, [onDone]);

  const typeLabel = APPLICATION_LABELS[row.application_type] || 'Application';
  const { fields, remarks, documents } = parseNotes(row.notes);
  const uploaded = (row.documents || []).map((doc) => DOC_LABELS[doc.doc_type] || doc.original_name);
  const visitDate = row.scheduled_date || row.preferred_date;
  const visitTime = row.scheduled_time || row.preferred_time;

  return createPortal(
    <div className="print-sheet">
      <section className="print-page">
        <header className="print-header">
          <div>
            <h1>AutoCompare</h1>
            <p>{typeLabel} Application Form</p>
          </div>
          <table className="print-meta">
            <tbody>
              <Row label="Application No." value={`#${row.id}`} />
              <Row label="Date submitted" value={formatDateTime(row.created_at)} />
              <Row label="Status" value={String(row.status || '').toUpperCase()} />
            </tbody>
          </table>
        </header>

        <h2>A. Applicant details</h2>
        <table className="print-table">
          <tbody>
            <Row label="Full name" value={row.customer_name} />
            <Row label="Mobile number" value={row.phone} />
            <Row label="Email" value={row.email} />
            <Row label="City" value={row.city} />
          </tbody>
        </table>

        <h2>B. Vehicle details</h2>
        <table className="print-table">
          <tbody>
            <Row label="Vehicle" value={row.vehicle_name} />
            {row.vehicle_variant ? <Row label="Variant" value={row.vehicle_variant} /> : null}
          </tbody>
        </table>

        {fields.length || remarks.length ? (
          <>
            <h2>C. {typeLabel} request</h2>
            <table className="print-table">
              <tbody>
                {fields.map(([label, value]) => (
                  <Row key={label} label={label} value={value} />
                ))}
                {remarks.length ? <Row label="Remarks" value={remarks.join(' ')} /> : null}
              </tbody>
            </table>
          </>
        ) : null}

        {visitDate ? (
          <>
            <h2>Appointment</h2>
            <table className="print-table">
              <tbody>
                <Row
                  label="Date / time"
                  value={`${visitDate}${visitTime ? ` at ${formatSlotTime(visitTime) || visitTime}` : ''}`}
                />
              </tbody>
            </table>
          </>
        ) : null}

        <h2>D. Documents enclosed</h2>
        {uploaded.length ? (
          <ol className="print-docs">
            {uploaded.map((label, i) => (
              <li key={`${label}-${i}`}>
                {label} <span>(Annexure {i + 1})</span>
              </li>
            ))}
          </ol>
        ) : (
          <p>{documents || 'No documents were uploaded.'}</p>
        )}

        <h2>E. Declaration</h2>
        <p className="print-declaration">
          I declare that the information given in this application and the documents attached are true and
          complete to the best of my knowledge, and I authorise verification of the same.
        </p>
        <div className="print-signatures">
          <div>
            <span />
            Applicant signature
          </div>
          <div>
            <span />
            Date
          </div>
        </div>

        <div className="print-office">
          <strong>For office use only</strong>
          <div className="print-signatures">
            <div>
              <span />
              Received by
            </div>
            <div>
              <span />
              Verified by
            </div>
            <div>
              <span />
              Date &amp; stamp
            </div>
          </div>
        </div>
      </section>

      {docPages.map((doc, i) => (
        <section key={doc.key} className="print-page print-annexure">
          <h3>
            Annexure {i + 1}: {doc.label}
            {doc.total > 1 ? ` (page ${doc.page} of ${doc.total})` : ''}
          </h3>
          <img src={doc.src} alt={doc.label} />
        </section>
      ))}
    </div>,
    document.body,
  );
};

export default ApplicationPrint;
